import { BaseColor, Color, ColorShade, rem, themeColor, ThemedComponentProps } from '~theme'
import { Template, ViewType } from './Button.types'

const coloredSolid = (color: Color, props: ThemedComponentProps) => ({
  static: {
    color: themeColor(color, ColorShade.foreground)(props),
    backgroundColor: themeColor(color)(props),
  },
  hover: {
    backgroundColor: themeColor(color, ColorShade.semiSoft)(props),
  },
  active: {
    backgroundColor: themeColor(color, ColorShade.soft)(props),
  },
  focus: {
    outlineColor: themeColor(color, ColorShade.stronger)(props),
  },
})

const coloredOutlined = (color: Color, props: ThemedComponentProps) => ({
  static: {
    color: themeColor(color)(props),
    borderColor: themeColor(color)(props),
  },
  hover: {
    color: themeColor(color, ColorShade.semiStrong)(props),
    borderColor: themeColor(color, ColorShade.semiStrong)(props),
  },
  active: {
    color: themeColor(color, ColorShade.strong)(props),
    borderColor: themeColor(color, ColorShade.strong)(props),
  },
  focus: {
    outlineColor: themeColor(color, ColorShade.strong)(props),
  },
})

const coloredTransparent = (color: Color, props: ThemedComponentProps) => ({
  static: {
    color: themeColor(color)(props),
  },
  hover: {
    color: themeColor(color, ColorShade.semiStrong)(props),
  },
  active: {
    color: themeColor(color, ColorShade.strong)(props),
  },
  focus: {
    outlineColor: themeColor(color, ColorShade.strong)(props),
  },
})

const statusColoredSolid = (color: Color, props: ThemedComponentProps) => ({
  static: {
    color: themeColor(color, ColorShade.foreground)(props),
    backgroundColor: themeColor(color)(props),
  },
  hover: {
    backgroundColor: themeColor(color, ColorShade.semiStrong)(props),
  },
  active: {
    backgroundColor: themeColor(color, ColorShade.semiSoft)(props),
  },
  focus: {
    outlineColor: themeColor(color, ColorShade.semiStrong)(props),
  },
})

const statusColoredTransparent = (color: Color, props: ThemedComponentProps) => ({
  static: {
    color: themeColor(color)(props),
  },
  hover: {
    color: themeColor(color, ColorShade.semiStrong)(props),
  },
  active: {
    color: themeColor(color, ColorShade.semiSoft)(props),
  },
  focus: {
    outlineColor: themeColor(color, ColorShade.semiStrong)(props),
  },
})

const statusColoredOutlined = (color: Color, props: ThemedComponentProps) => ({
  static: {
    color: themeColor(color)(props),
    borderColor: themeColor(color)(props),
  },
  hover: {
    color: themeColor(color, ColorShade.semiStrong)(props),
    borderColor: themeColor(color, ColorShade.semiStrong)(props),
  },
  active: {
    color: themeColor(color, ColorShade.semiSoft)(props),
    borderColor: themeColor(color, ColorShade.semiSoft)(props),
  },
  focus: {
    outlineColor: themeColor(color, ColorShade.semiStrong)(props),
  },
})

export const makeTemplates = (props: ThemedComponentProps): Record<ViewType, Template> => ({
  /** Primary */
  'primary-solid': coloredSolid(Color.primary, props),
  'primary-outlined': coloredOutlined(Color.primary, props),
  'primary-transparent': coloredTransparent(Color.primary, props),

  /** Base */
  'base-outlined': {
    static: {
      color: themeColor(BaseColor.shade_60)(props),
      borderColor: themeColor(BaseColor.shade_60)(props),
    },
    hover: {
      color: themeColor(BaseColor.shade_80)(props),
      borderColor: themeColor(BaseColor.shade_60)(props),
    },
    active: {
      color: themeColor(BaseColor.shade_90)(props),
      borderColor: themeColor(BaseColor.shade_90)(props),
    },
    focus: {
      outlineColor: themeColor(BaseColor.shade_90)(props),
    },
  },

  'base-transparent': {
    static: {
      color: themeColor(BaseColor.shade_60)(props),
    },
    hover: {
      color: themeColor(BaseColor.shade_80)(props),
    },
    active: {
      color: themeColor(BaseColor.shade_90)(props),
    },
    focus: {
      outlineColor: themeColor(BaseColor.shade_90)(props),
    },
  },

  /** Danger */
  'danger-solid': statusColoredSolid(Color.danger, props),
  'danger-outlined': statusColoredOutlined(Color.danger, props),
  'danger-transparent': statusColoredTransparent(Color.danger, props),

  /** Success */
  'success-solid': statusColoredSolid(Color.success, props),
  'success-outlined': statusColoredOutlined(Color.success, props),
  'success-transparent': statusColoredTransparent(Color.success, props),

  /** Warning */
  'warning-solid': statusColoredSolid(Color.warning, props),
  'warning-outlined': statusColoredOutlined(Color.warning, props),
  'warning-transparent': statusColoredTransparent(Color.warning, props),

  /** Info */
  'info-solid': statusColoredSolid(Color.info, props),
  'info-outlined': statusColoredOutlined(Color.info, props),
  'info-transparent': statusColoredTransparent(Color.info, props),
})

export const sizeList = {
  'extra-small': rem(24),
  small: rem(32),
  medium: rem(40),
  large: rem(44),
}

export const paddingList = {
  'extra-small': rem(8),
  small: rem(13),
  medium: rem(17),
  large: rem(19),
}

export const iconOffsetList = {
  'extra-small': rem(-2),
  small: rem(-2),
  medium: rem(-2),
  large: rem(-4),
}
